import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '../../shared/services/config/config.service';
import { Observable } from 'rxjs';
import {
  buildQueryParams,
  PaginationParams,
} from '../../shared/utils/http-utils';
import {
  IAssinarDocumentoRequest,
  IDocumentoPendentePermissao,
  IDocumentoPermissao,
  IEmitirDocumentoPermissaoRequest,
  IEmitirDocumentoPermissaoResponse,
  IPagedResponse,
  IPortadorPermissao,
  ITipoAssinante,
  IUsuarioAssinante,
  IValidarEmissaoCarteiraRequest,
  IValidarEmissaoCarteiraResponse,
} from '../models/documento-permissao.interface';

interface UsuariosAssinantesFilters extends PaginationParams {
  nome?: string | null;
}

interface DocumentosPendentesPermissaoFilters extends PaginationParams {
  tipoDocumento?: string | null;
  nomeAssinante?: string | null;
}

interface DocumentosPermissaoFilters extends PaginationParams {
  tipoDocumento?: string | null;
  status?: string | null;
  nomeAssinante?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class PermissaoService {
  private baseUrl: string;

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
  ) {
    this.baseUrl = this.configService.pessoaApi;
  }

  getPermissoes(
    pageSize: number = 10,
    pageIndex: number = 0,
    sortDirection: string = 'asc',
    sortProperty: string = 'numeroPermissao',
    permissaoSearchTerm: string = '',
    status: string = '',
    categoria: string = '',
    inicioPermissao_start: string = '',
    inicioPermissao_end: string = '',
    inicioReserva_start: string = '',
    inicioReserva_end: string = '',
    finalReserva_start: string = '',
    finalReserva_end: string = '',
  ) {
    return this.http.get(
      this.baseUrl +
      `/v1/permissao?size=${pageSize}` +
      `&page=${pageIndex}` +
      `&sortField=${sortProperty}` +
      `&sortDirection=${sortDirection}` +
      `&numeroPermissao=${permissaoSearchTerm}` +
      `&status=${status}` +
      `&licenseCategory=${categoria}` +
      `&inicioPermissao_start=${inicioPermissao_start}` +
      `&inicioPermissao_end=${inicioPermissao_end}` +
      `&inicioReserva_start=${inicioReserva_start}` +
      `&inicioReserva_end=${inicioReserva_end}` +
      `&finalReserva_start=${finalReserva_start}` +
      `&finalReserva_end=${finalReserva_end}`,
    );
  }

  getPermissoesAuxiliar(numeroPermissao: string = '') {
    return this.http.get(
      this.baseUrl +
      `/v1/permissao?numeroPermissao=${numeroPermissao}` +
      `&showPermissionarios=true` +
      `&excluiPorLimiteDeAuxiliares=true`,
    );
  }

  getPermissao(id: string | null) {
    return this.http.get(this.baseUrl + `/v1/permissao/${id}`);
  }

  getHistoricProcessoSei(idPermissao: string , idSei: string) {
    return this.http.get(this.baseUrl 
      + `/v1/permissao/${idPermissao}/processos-sei/${idSei}/historico?page=0&size=10&sortField=data&sortDirection=desc`);
  }

  getProcessoSei(id: string | null, pageSize: number = 10,
    pageIndex: number = 0,
    sortDirection: string = 'asc',
    sortProperty: string = 'numeroPermissao') {
    return this.http.get(this.baseUrl + `/v1/permissao/${id}/processos-sei?page=${pageIndex}&size=${pageSize}&sortField=${sortProperty}&sortDirection=${sortDirection}`);
  }

  getProcessoSeiById(id: string | null, processoSeiId: string | null) {
    return this.http.get(this.baseUrl + `/v1/permissao/${id}/processos-sei/${processoSeiId}`);
  }

  deleteProcessoSei(permissaoId: string | null = null, seiId: string) {
    return this.http.delete(this.baseUrl + `/v1/permissao/${permissaoId}/processos-sei/${seiId}`);
  }

  getDocumentosSei(permissaoId: string | null = null, processoSeiId: string | null = null, pageSize: number = 10,
    pageIndex: number = 0,
    sortDirection: string = 'asc',
    sortProperty: string = 'numeroPermissao') {
    return this.http.get(this.baseUrl + `/v1/permissao/${permissaoId}/processos-sei/${processoSeiId}/documentos?page=${pageIndex}&size=${pageSize}&sortField=${sortProperty}&sortDirection=${sortDirection}`);
  }

  getMaxDocumentosSei(permissaoId: string | null = null, processoSeiId: string | null = null, pageSize: number = 500,
    pageIndex: number = 0,
    sortDirection: string = 'asc',
    sortProperty: string = 'numeroPermissao') {
    return this.http.get(this.baseUrl + `/v1/permissao/${permissaoId}/processos-sei/${processoSeiId}/documentos?page=${pageIndex}&size=${pageSize}&sortField=${sortProperty}&sortDirection=${sortDirection}`);
  }

  downloadAllDocumentosSei(permissaoId: string | null = null, processoSeiId: string | null = null) {
    return this.http.get(this.baseUrl + `/v1/permissao/${permissaoId}/processos-sei/${processoSeiId}/download`, {
      responseType: 'blob' as 'json',
      observe: 'response',
    });
  }
  downloadDocumentoSei(seiId: string | null = null, idDocumento: string | null = null) {
    return this.http.get(this.baseUrl + `/v1/permissao/processos-sei/${seiId}/download/${idDocumento}`);

  }

  create(data: any) {
    return this.http.post(this.baseUrl + `/v1/permissao`, data);
  }

  edit(id: string | null, data: any) {
    return this.http.patch(this.baseUrl + `/v1/permissao/${id}`, data);
  }

  getHistorico(
    id: string,
    pageIndex: number = 0,
    sortDirection: string = 'desc',
  ) {
    return this.http.get(
      this.baseUrl +
      `/v1/permissao/${id}/historico?&page=${pageIndex}&sortDirection=${sortDirection}`,
    );
  }

  delete(id: string) {
    return this.http.delete(this.baseUrl + `/v1/permissao/${id}`);
  }

  createPermissionario(id: string | null, data: any) {
    return this.http.post(
      this.baseUrl + `/v1/permissao/${id}/permissionario`,
      data,
    );
  }

  updatePermissionario(id: string | null, data: any) {
    return this.http.patch(
      this.baseUrl + `/v1/permissao/${id}/permissionario`,
      data,
    );
  }

  getCategorias() {
    return this.http.get(this.baseUrl + `/v1/permissao/categoria`);
  }

  getCategoriaHabilitacao() {
    return this.http.get(this.baseUrl + `/v1/habilitacao/categoria`);
  }

  getTipoEndereco() {
    return this.http.get(this.baseUrl + `/v1/endereco/tipo`);
  }

  getEstados() {
    return this.http.get(this.baseUrl + `/v1/endereco/estado`);
  }

  getCidades(estadoId: string) {
    return this.http.get(
      this.baseUrl + `/v1/endereco/cidade?&idEstado=${estadoId}`,
    );
  }

  getTipoContato() {
    return this.http.get(this.baseUrl + `/v1/contato/tipo`);
  }

  performTaxiTransition(idExterno: string, transicaoId: string) {
    return this.http.post(this.baseUrl + `/v1/permissao/transicao`, {
      idExterno,
      transicaoId,
    });
  }

  editAssinantes(form: any) {
    return this.http.patch(this.baseUrl + `/v1/permissao/assinantes`, form);
  }

  createAssinantes(form: any) {
    return this.http.post(this.baseUrl + `/v1/permissao/assinantes`, form);
  }

  getAssinantesByDocId(
    docId: string,
    pageSize: number = 10,
    pageIndex: number = 0,
    sortDirection: string = 'desc',
    sortProperty: string = 'dataCadastro',
  ) {
    return this.http.get(
      this.baseUrl +
      `/v1/permissao/documentos/${docId}/assinantes?size=${pageSize}&page=${pageIndex}&sortField=${sortProperty}&sortDirection=${sortDirection}`,
    );
  }

  getDocumentoById(id: any) {
    return this.http.get(this.baseUrl + `/v1/permissao/documentos/${id}`);
  }

  downloadExportExcel(
    sortDirection: string = 'asc',
    sortProperty: string = 'numeroPermissao',
    permissaoSearchTerm: string = '',
    status: string = '',
    categoria: string = '',
    inicioPermissao_start: string = '',
    inicioPermissao_end: string = '',
    inicioReserva_start: string = '',
    inicioReserva_end: string = '',
    finalReserva_start: string = '',
    finalReserva_end: string = '',
  ): Observable<HttpResponse<Blob>> {
    let params = new HttpParams()
      .set('sortDirection', sortDirection)
      .set('sortProperty', sortProperty)
      .set('numeroPermissao', permissaoSearchTerm)
      .set('status', status)
      .set('licenseCategory', categoria)
      .set('inicioPermissao_start', inicioPermissao_start)
      .set('inicioPermissao_end', inicioPermissao_end)
      .set('inicioReserva_start', inicioReserva_start)
      .set('inicioReserva_end', inicioReserva_end)
      .set('finalReserva_start', finalReserva_start)
      .set('finalReserva_end', finalReserva_end);

    return this.http.get<Blob>(
      `${this.baseUrl}/v1/permissao/exportar-dados-csv`,
      {
        params,
        observe: 'response',
        responseType: 'blob' as 'json',
      },
    );
  }

  createProcessoSei(permissaoId: string | null, data: any) {

    return this.http.post(this.baseUrl + `/v1/permissao/${permissaoId}/processos-sei`, data);
  }

  editProcessoSei(permissaoId: string | null, processoSeiId: string | null, data: any) {
    return this.http.put(this.baseUrl + `/v1/permissao/${permissaoId}/processos-sei/${processoSeiId}`, data);
  }

  salvarReserva(permissaoId: string | null, from: any) {
    return this.http.post(this.baseUrl + `/v1/permissao/${permissaoId}/reservas`, from);
  }

  editarReserva(idPermissao: string | null,  id: string | null, from: any) {
    return this.http.put(this.baseUrl + `/v1/permissao/${idPermissao}/reservas/${id}`, from);
  }

  getGestaoReservas(permissaoId: string, pageIndex: number = 0, pageSize: number = 10, sortDirection: string = 'desc', sortProperty: string = 'dataInicioReserva') {
    return this.http.get(this.baseUrl + `/v1/permissao/${permissaoId}/reservas?size=${pageSize}&page=${pageIndex}&sortField=${sortProperty}&sortDirection=${sortDirection}`);
  }
  getGestaoReserva(permissaoId: string | null, reservaId:string | null) {
    return this.http.get(this.baseUrl + `/v1/permissao/${permissaoId}/reservas/${reservaId}`);
  }

  deleteGestaoReserva(permissaoId: string | null, reservaId: string) {
    return this.http.delete(this.baseUrl + `/v1/permissao/${permissaoId}/reservas/${reservaId}`);
  }

  getHistoricGestaoReserva(permissaoId: string, reservaId: string, pageIndex: number = 0, sortDirection: string = 'desc') {
    return this.http.get(this.baseUrl + `/v1/permissao/${permissaoId}/reservas/${reservaId}/historico?&page=${pageIndex}&sortDirection=${sortDirection}`);
  }

  getPlacasReservas(permissaoId: string | null) {
    return this.http.get(this.baseUrl + `/v1/permissao/${permissaoId}/reservas/placas-veiculos-ativos`);
  }

  // 1.1 GET /taxis/usuarios/assinantes
  getUsuariosAssinantes(
    filters: UsuariosAssinantesFilters,
  ): Observable<IPagedResponse<IUsuarioAssinante>> {
    return this.http.get<IPagedResponse<IUsuarioAssinante>>(
      `${this.baseUrl}/v1/taxis/usuarios/assinantes`,
      {
        params: buildQueryParams(filters),
        headers: new HttpHeaders({ 'x-skip-loader': 'true' }),
      },
    );
  }

  // 1.2 POST /taxis/permissoes-auxiliares
  getPortadoresPermissao(
    numeroPermissao: string,
  ): Observable<IPortadorPermissao[]> {
    return this.http.post<IPortadorPermissao[]>(
      this.baseUrl + `/v1/taxis/permissoes-auxiliares`,
      { numeroPermissao },
    );
  }

  getTiposAssinante(): Observable<ITipoAssinante[]> {
    return this.http.get<ITipoAssinante[]>(
      `${this.baseUrl}/v1/taxis/documentos/tipos-assinante`,
    );
  }

  // Responde 200 com o diagnóstico; o consumidor bloqueia a emissão quando `apto` é false.
  validarEmissaoCarteira(
    data: IValidarEmissaoCarteiraRequest,
  ): Observable<IValidarEmissaoCarteiraResponse> {
    return this.http.post<IValidarEmissaoCarteiraResponse>(
      `${this.baseUrl}/v1/taxis/carteira-condutor/validar-emissao`,
      data,
    );
  }

  // 2.1 POST /permissao/{permissaoId}/documentos
  emitirDocumento(
    permissaoId: string,
    data: IEmitirDocumentoPermissaoRequest,
  ): Observable<IEmitirDocumentoPermissaoResponse> {
    return this.http.post<IEmitirDocumentoPermissaoResponse>(
      `${this.baseUrl}/v1/permissao/${permissaoId}/documentos`,
      data,
    );
  }

  // 2.2 PATCH /permissao/{permissaoId}/documentos/assinantes
  // Mesmo corpo da emissão: o documento é resolvido pelo par (permissão,
  // tipoDocumento) — e por portador, na Carteira de Condutor.
  editarAssinantesDocumento(
    permissaoId: string,
    data: IEmitirDocumentoPermissaoRequest,
  ): Observable<IEmitirDocumentoPermissaoResponse> {
    return this.http.patch<IEmitirDocumentoPermissaoResponse>(
      `${this.baseUrl}/v1/permissao/${permissaoId}/documentos/assinantes`,
      data,
    );
  }

  // 2.3 GET /permissao/{permissaoId}/documentos-pendentes-assinatura
  getDocumentosPendentesAssinatura(
    permissaoId: string,
    filters: DocumentosPendentesPermissaoFilters,
  ): Observable<IPagedResponse<IDocumentoPendentePermissao>> {
    return this.http.get<IPagedResponse<IDocumentoPendentePermissao>>(
      `${this.baseUrl}/v1/permissao/${permissaoId}/documentos-pendentes-assinatura`,
      { params: buildQueryParams(filters) },
    );
  }

  // 2.4 GET /permissao/{permissaoId}/documentos
  getDocumentosPermissao(
    permissaoId: string,
    filters: DocumentosPermissaoFilters,
  ): Observable<IPagedResponse<IDocumentoPermissao>> {
    return this.http.get<IPagedResponse<IDocumentoPermissao>>(
      `${this.baseUrl}/v1/permissao/${permissaoId}/documentos`,
      { params: buildQueryParams(filters) },
    );
  }

  // 2.5 POST /taxis/documentos/assinar
  assinarDocumento(data: IAssinarDocumentoRequest) {
    return this.http.post(`${this.baseUrl}/v1/taxis/documentos/assinar`, data);
  }

  // 2.6 GET /taxis/documentos/{documentoId}/download
  downloadDocumento(documentoId: string): Observable<HttpResponse<Blob>> {
    return this.http.get<Blob>(
      `${this.baseUrl}/v1/taxis/documentos/${documentoId}/download`,
      { observe: 'response', responseType: 'blob' as 'json' },
    );
  }

  // 2.7 GET /taxis/documentos/{documentoId}/historico
  // Assinatura (id, pageIndex) compatível com ModalHistoricComponent.
  getHistoricoDocumento(
    documentoId: string,
    pageIndex: number = 0,
    sortDirection: string = 'desc',
  ) {
    return this.http.get(
      `${this.baseUrl}/v1/taxis/documentos/${documentoId}/historico`,
      {
        params: buildQueryParams({
          page: pageIndex,
          sortField: 'data',
          sortDirection,
        }),
      },
    );
  }
}
